/**
 * LivretInteractif.jsx — Composant partagé rendu d'un livret interactif.
 *
 * Prend en entrée un dataset (chapitres avec sections d'items cochables)
 * et affiche :
 *  - Bandeau cross-navigation Trame ⇄ Livret
 *  - Barre de progression globale
 *  - Sommaire latéral (Sheet) sur mobile
 *  - Navigation chapitre par chapitre
 *  - Items cochables avec persistance localStorage
 *  - Boutons impression / PDF
 *
 * 100% mobile-first : optimisé pour l'élève qui coche sur son téléphone
 * pendant ou après les séances.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, Menu, X, Home, CheckCircle2, Circle,
  Sparkles, Printer, User as UserIcon, Cloud, QrCode,
} from 'lucide-react';
import TrameLivretCross from '../trames/TrameLivretCross';
import LivretSessionRecap from './LivretSessionRecap';
import SaveProgressCTA from './SaveProgressCTA';
import LivretQrShareModal from './LivretQrShareModal';
import useLivretSync from '../../hooks/useLivretSync';
import { useApp } from '../../contexts/AppContext';

function Progress({ done, total, color = '#e11d48' }) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <div className="w-full">
      <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-widest text-white/60 mb-1">
        <span>Progression</span>
        <span style={{ color }}>{done} / {total} · {pct} %</span>
      </div>
      <div className="h-2 rounded-full bg-white/10 overflow-hidden">
        <div className="h-full transition-all" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

export default function LivretInteractif({ data, testid = 'livret-interactif' }) {
  const { storageKey, title, subtitle, chapters, trameTo, trameLabel, livretTo, livretLabel, trameKey, competences } = data;

  const { user } = useApp();
  const { state, setValue, saveProfil, done, syncing } = useLivretSync(storageKey);

  // Deep-link : ?chapter=<id> ouvre directement le bon chapitre
  const initialIdx = (() => {
    if (typeof window === 'undefined') return 0;
    const p = new URLSearchParams(window.location.search);
    const wanted = p.get('chapter');
    if (!wanted) return 0;
    const idx = chapters.findIndex((c) => c.id === wanted);
    return idx >= 0 ? idx : 0;
  })();
  const [chapterIdx, setChapterIdx] = useState(initialIdx);
  const [menuOpen, setMenuOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [dismissedNext, setDismissedNext] = useState({});

  const profil = state.__profil || { eleve: '', moniteur: '', debut: '' };

  useEffect(() => {
    document.title = `${title} · Livret interactif · code2roues`;
  }, [title]);

  // Sync URL + scroll top à chaque bascule de chapitre
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const p = new URLSearchParams(window.location.search);
    const currentQ = p.get('chapter');
    const wantedQ = chapters[chapterIdx]?.id;
    if (currentQ !== wantedQ && wantedQ) {
      p.set('chapter', wantedQ);
      window.history.replaceState(null, '', window.location.pathname + '?' + p.toString());
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [chapterIdx, chapters]);

  const current = chapters[chapterIdx];
  const prev = () => setChapterIdx(i => Math.max(0, i - 1));
  const next = () => setChapterIdx(i => Math.min(chapters.length - 1, i + 1));

  const total = useMemo(() => {
    let count = 0;
    chapters.forEach(ch => (ch.sections || []).forEach(s => count += (s.items || []).length));
    return count;
  }, [chapters]);

  // Progression par chapitre → badges dans sommaire + CTA fin de chapitre
  const perChapterProgress = useMemo(() => {
    const map = {};
    chapters.forEach((ch, ci) => {
      let d = 0, t = 0;
      (ch.sections || []).forEach((s, si) => {
        (s.items || []).forEach((_, i) => {
          t += 1;
          if (state[`${ch.id}:${si}:${i}`] === true) d += 1;
        });
      });
      map[ch.id] = { done: d, total: t, complete: t > 0 && d === t };
    });
    return map;
  }, [state, chapters]);

  const currentChapterProgress = perChapterProgress[current.id];
  const isLastChapter = chapterIdx === chapters.length - 1;
  const showNextChapterCTA =
    currentChapterProgress?.complete &&
    !isLastChapter &&
    !dismissedNext[current.id];

  const Icon = current.icon;
  const color = current.color || '#e11d48';

  return (
    <div className="min-h-screen bg-neutral-950 text-white pb-24 lg:pb-16" data-testid={testid}>
      <TrameLivretCross
        current="livret"
        trameTo={trameTo}
        trameLabel={trameLabel}
        livretTo={livretTo}
        livretLabel={livretLabel}
      />

      {/* CTA « Créer un compte pour sauvegarder » — uniquement visiteurs non connectés */}
      <SaveProgressCTA done={done} />

      {/* Barre supérieure sticky */}
      <header className="sticky top-0 z-30 bg-neutral-950/95 backdrop-blur border-b border-white/10">
        <div className="max-w-4xl mx-auto px-3 py-2.5 flex items-center gap-2">
          <button onClick={() => setMenuOpen(true)} className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white/85 text-[12px] font-bold transition" data-testid="livret-btn-menu">
            <Menu className="w-3.5 h-3.5" /> Sommaire
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-black uppercase tracking-widest text-white/50 truncate flex items-center gap-1.5">
              {title}
              {user && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-[8.5px]" data-testid="livret-cloud-badge" title={syncing ? 'Synchronisation…' : 'Progression synchronisée'}>
                  <Cloud className="w-2.5 h-2.5" /> {syncing ? 'Sync…' : 'Cloud'}
                </span>
              )}
            </div>
            <div className="text-[13px] font-black text-white truncate leading-tight">{current.title}</div>
          </div>
          <button
            onClick={() => setQrOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-rose-500/15 hover:bg-rose-500/25 border border-rose-500/40 text-rose-200 text-[12px] font-black uppercase tracking-wider transition"
            data-testid="livret-btn-qr"
            title="Partager par QR code (élève sur son téléphone)"
          >
            <QrCode className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">QR élève</span>
          </button>
          <button onClick={() => window.print()} className="hidden sm:inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white/85 text-[12px] font-bold transition" data-testid="livret-btn-print">
            <Printer className="w-3.5 h-3.5" /> Imprimer
          </button>
        </div>
        <div className="max-w-4xl mx-auto px-3 pb-2">
          <Progress done={done} total={total} color={color} />
        </div>
      </header>

      {/* Sheet Sommaire */}
      {menuOpen && (
        <div className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm" onClick={() => setMenuOpen(false)}>
          <div className="absolute left-0 top-0 bottom-0 w-[85vw] max-w-sm bg-neutral-950 border-r border-white/10 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-neutral-950 px-4 py-3 border-b border-white/10 flex items-center justify-between">
              <div className="text-[10px] font-black uppercase tracking-widest text-white/50">Sommaire</div>
              <button onClick={() => setMenuOpen(false)} className="w-8 h-8 rounded-lg hover:bg-white/10 flex items-center justify-center"><X className="w-4 h-4" /></button>
            </div>
            <ul className="p-2 space-y-1">
              {chapters.map((c, i) => {
                const CIcon = c.icon;
                const active = i === chapterIdx;
                const chP = perChapterProgress[c.id];
                const badge = chP?.total > 0
                  ? (chP.complete ? '✓' : `${chP.done}/${chP.total}`)
                  : null;
                return (
                  <li key={c.id}>
                    <button onClick={() => { setChapterIdx(i); setMenuOpen(false); }} className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-left transition ${active ? 'bg-white/10 border border-white/20' : 'hover:bg-white/5 border border-transparent'}`} data-testid={`livret-toc-${c.id}`}>
                      <span className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${c.color}22`, border: `1px solid ${c.color}55` }}>
                        <CIcon className="w-4 h-4" style={{ color: c.color }} />
                      </span>
                      <div className="min-w-0 flex-1">
                        {c.n && <div className="text-[10px] font-mono font-black text-white/50">{c.n}</div>}
                        <div className="text-[13px] font-black text-white leading-tight">{c.title}</div>
                      </div>
                      {badge && (
                        <span
                          className={`shrink-0 text-[10px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full ${
                            chP.complete
                              ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                              : 'bg-white/[0.06] text-white/60 border border-white/15'
                          }`}
                          data-testid={`livret-toc-badge-${c.id}`}
                        >
                          {badge}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}

      {/* Contenu chapitre */}
      <main className="max-w-4xl mx-auto px-3 sm:px-6 py-6">
        {/* Récap auto-évaluation depuis la trame — visible uniquement au 1er chapitre
            pour ne pas encombrer les autres pages. */}
        {trameKey && chapterIdx === 0 && (
          <LivretSessionRecap
            trameKey={trameKey}
            trameTo={trameTo}
            exercises={competences || []}
          />
        )}

        {/* Hero chapitre */}
        <div className="rounded-2xl p-5 mb-5 border" style={{ background: `${color}12`, borderColor: `${color}44` }}>
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <span className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${color}25`, border: `1px solid ${color}55` }}>
              <Icon className="w-5 h-5" style={{ color }} />
            </span>
            <div className="min-w-0">
              {current.n && <div className="text-[10px] font-mono font-black tracking-widest" style={{ color }}>Chapitre {current.n}</div>}
              <h1 className="text-white text-2xl sm:text-3xl font-black leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                {current.title}
              </h1>
            </div>
          </div>
          {current.intro && <p className="text-white/75 text-[13.5px] leading-relaxed">{current.intro}</p>}
        </div>

        {/* Profil (chapitre couverture) */}
        {current.profil && (
          <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 mb-5">
            <div className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-white/60 mb-3"><UserIcon className="w-3 h-3" /> Mon profil</div>
            <div className="grid sm:grid-cols-3 gap-3">
              <label className="block">
                <span className="text-[10.5px] font-black uppercase tracking-widest text-white/60">Prénom élève</span>
                <input type="text" value={profil.eleve} onChange={(e) => saveProfil({ ...profil, eleve: e.target.value })} className="mt-1 w-full h-10 rounded-lg bg-black/40 border border-white/15 px-3 text-white text-sm focus:border-white/30 outline-none" data-testid="livret-profil-eleve" placeholder="Ton prénom" />
              </label>
              <label className="block">
                <span className="text-[10.5px] font-black uppercase tracking-widest text-white/60">Moniteur</span>
                <input type="text" value={profil.moniteur} onChange={(e) => saveProfil({ ...profil, moniteur: e.target.value })} className="mt-1 w-full h-10 rounded-lg bg-black/40 border border-white/15 px-3 text-white text-sm focus:border-white/30 outline-none" data-testid="livret-profil-moniteur" placeholder="Nom du moniteur" />
              </label>
              <label className="block">
                <span className="text-[10.5px] font-black uppercase tracking-widest text-white/60">Début formation</span>
                <input type="date" value={profil.debut} onChange={(e) => saveProfil({ ...profil, debut: e.target.value })} className="mt-1 w-full h-10 rounded-lg bg-black/40 border border-white/15 px-3 text-white text-sm focus:border-white/30 outline-none" data-testid="livret-profil-debut" />
              </label>
            </div>
          </div>
        )}

        {/* Sections cochables */}
        <div className="space-y-4">
          {(current.sections || []).map((sec, si) => (
            <section key={si} className="rounded-2xl bg-white/[0.03] border border-white/10 p-4">
              <h2 className="text-white font-black text-[15px] sm:text-[17px] mb-3" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{sec.title}</h2>
              <ul className="space-y-1.5">
                {sec.items.map((it, i) => {
                  const key = `${current.id}:${si}:${i}`;
                  const checked = state[key] === true;
                  return (
                    <li key={i}>
                      <button
                        type="button"
                        onClick={() => setValue(key, !checked)}
                        className={`w-full flex items-start gap-3 px-3 py-2.5 rounded-xl text-left transition ${checked ? 'bg-emerald-500/10 border border-emerald-500/30' : 'hover:bg-white/5 border border-transparent'}`}
                        data-testid={`livret-item-${key.replace(/:/g, '-')}`}
                      >
                        {checked
                          ? <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                          : <Circle className="w-5 h-5 text-white/30 flex-shrink-0 mt-0.5" />
                        }
                        <span className={`text-[13.5px] leading-relaxed flex-1 ${checked ? 'text-emerald-50' : 'text-white/85'}`}>{it}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>

        {/* CTA « Chapitre complet → passer au suivant » — apparaît
            uniquement quand tous les items sont cochés + non déjà dismissé */}
        {showNextChapterCTA && (
          <div className="mt-6 rounded-2xl border-2 p-5 relative animate-in fade-in slide-in-from-bottom-2 duration-300" style={{ borderColor: `${color}66`, background: `linear-gradient(135deg, ${color}22, transparent)` }} data-testid="livret-cta-chapter-complete">
            <button
              onClick={() => setDismissedNext((d) => ({ ...d, [current.id]: true }))}
              className="absolute top-2 right-2 w-7 h-7 rounded-lg hover:bg-white/10 flex items-center justify-center text-white/50 hover:text-white/80 transition"
              aria-label="Masquer"
              data-testid="livret-cta-dismiss"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="flex items-start gap-3 mb-3">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${color}33`, border: `1px solid ${color}66` }}>
                <Sparkles className="w-5 h-5" style={{ color }} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] font-black uppercase tracking-widest mb-1" style={{ color }}>Chapitre validé !</div>
                <div className="text-white font-black text-lg leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                  Tu maîtrises tout le chapitre {current.n || chapterIdx + 1}.
                </div>
                <p className="text-white/70 text-[13px] mt-1">Prêt à passer au chapitre suivant ?</p>
              </div>
            </div>
            <button
              onClick={next}
              className="w-full sm:w-auto inline-flex items-center gap-1.5 h-11 px-5 rounded-xl text-white text-[12px] font-black uppercase tracking-widest transition hover:opacity-90"
              style={{ background: color }}
              data-testid="livret-cta-next-chapter"
            >
              Chapitre suivant <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* Bandeau félicitations sur dernier chapitre si 100% */}
        {chapterIdx === chapters.length - 1 && done === total && total > 0 && (
          <div className="mt-6 rounded-2xl border border-emerald-500/40 bg-gradient-to-br from-emerald-950/40 to-transparent p-5 text-center">
            <Sparkles className="w-6 h-6 text-emerald-300 mx-auto mb-2" />
            <div className="text-emerald-100 font-black text-lg" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Bravo, ton livret est complet !</div>
            <div className="text-emerald-200/80 text-[12.5px] mt-1">Toutes les compétences sont validées. Prends la route en confiance.</div>
          </div>
        )}

        {/* Navigation chapitres */}
        <div className="mt-8 flex items-center justify-between gap-3">
          <button onClick={prev} disabled={chapterIdx === 0} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-white text-[12px] font-black uppercase tracking-widest transition disabled:opacity-40 disabled:pointer-events-none" data-testid="livret-btn-prev">
            <ChevronLeft className="w-3.5 h-3.5" /> Chapitre préc.
          </button>
          <Link to="/formations-2-roues" className="hidden sm:inline-flex items-center gap-1.5 h-10 px-3 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-white/70 text-[11px] font-black uppercase tracking-widest transition">
            <Home className="w-3.5 h-3.5" /> Hub
          </Link>
          <button onClick={next} disabled={chapterIdx === chapters.length - 1} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl text-white text-[12px] font-black uppercase tracking-widest transition disabled:opacity-40 disabled:pointer-events-none" style={{ background: color }} data-testid="livret-btn-next">
            Chapitre suiv. <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </main>

      {/* Modale QR — partage du livret sur smartphone élève */}
      <LivretQrShareModal
        open={qrOpen}
        onClose={() => setQrOpen(false)}
        livretPath={livretTo || '/'}
        livretTitle={title}
        livretSubtitle={subtitle}
        accentColor={color || '#f43f5e'}
        tabletMode
        bypassAuth
      />
    </div>
  );
}
